import { Component, Element, Event, EventEmitter, Host, Prop, State, h } from '@stencil/core';

export interface AhaColorPicker extends HTMLAhaColorPickerElement {}

const COLORS = [
  'FFE9E6', // Garnet-50
  'FFF3DC', // Gold-50
  'FFF9D8', // Citrine-50
  'F7FFC9', // Peridot-50
  'F0FFD1', // Jade-50
  'DCFFF0', // Emerald-50
  'D9FAFF', // Topaz-50
  'E0EEF9', // Blue-50
  'EADAFF', // Amethyst-50
  'FFE2FC', // Tourmaline-50
  'FFE1EC', // Rhodonite-50
  'CDCDCD', // Gray-500
  'FFFFFF', // #FFFFFF

  'FF9183', // Garnet-300
  'FFD481', // Gold-300
  'FFED8B', // Citrine-300
  'E3F864', // Peridot-300
  'CDFC6D', // Jade-200
  'A1FFD7', // Emerald-200
  'A0F4FF', // Topaz-200
  'B1D4F5', // Blue-200
  'CCA8FF', // Amethyst-200
  'F59AEC', // Tourmaline-200
  'FF99BE', // Rhodonite-200
  'B5B5B5', // Gray-600
  'F7F7F7', // Gray-100

  'F97E6E', // Garnet-400
  'FFC655', // Gold-400
  'F6DC52', // Citrine-500
  'CCE52D', // Peridot-500
  'A3DF2B', // Jade-400
  '24CF87', // Emerald-500
  '41E7E7', // Topaz-400
  '5CA5E0', // Blue-400
  'B27EFA', // Amethyst-300
  'E852D9', // Tourmaline-400
  'F56E9F', // Rhodonite-300
  '999999', // Gray-700
  'F1F1F1', // Gray-200

  'EF422B', // Garnet-600
  'E59904', // Gold-600
  'DEC128', // Citrine-700
  'ACC60E', // Peridot-700
  '81B912', // Jade-600
  '16A468', // Emerald-600
  '0CB4B4', // Topaz-600
  '0073CF', // Blue-500
  '854AD7', // Amethyst-400
  'D02EB6', // Tourmaline-500
  'DC5686', // Rhodonite-400
  '666666', // Gray-800
  'E9E9E9', // Gray-300

  'C9250E', // Garnet-700
  'B47700', // Gold-800
  'B09506', // Citrine-900
  '728500', // Peridot-900
  '5E8C05', // Jade-800
  '018454', // Emerald-800
  '007777', // Topaz-800
  '0061AE', // Blue-600
  '662DB5', // Amethyst-500
  'BB0AA9', // Tourmaline-600
  'CA2560', // Rhodonite-500
  '333333', // Gray-900
  'E1E1E1', // Gray-400
];

/**
 * A component to make selecting colors easy.
 */
@Component({
  tag: 'aha-color-picker',
  styleUrl: 'aha-color-picker.css',
  shadow: false,
  scoped: true,
})
export class AhaColorPicker {
  colors = [
    'peridot',
    'jade',
    'emerald',
    'topaz',
    'blue',
    'lapis',
    'amethyst',
    'tourmaline',
    'rhodonite',
    'garnet',
    'gold',
    'citrine',
  ];
  @Element() host!: HTMLAhaColorPickerElement;

  private menuContentElem?: HTMLAhaMenuContentElement;

  /**
   * Triggers when the value is changed.
   */
  @Event({
    eventName: 'aha-color-picker:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  /**
   * Triggers when the color picker menu is initially opened but before it is visible.
   */
  @Event({
    eventName: 'aha-color-picker:open',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   *
   * Triggers when the color-picker menu is visible on the page.
   */
  @Event({
    eventName: 'aha-color-picker:opened',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers when the color picker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-color-picker:close',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers when the color picker menu is no longer visible on the page.
   */
  @Event({
    eventName: 'aha-color-picker:closed',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /**
   * Triggers when the color picker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-color-picker:cancel',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /** The name of the form control that will be submitted via a regular `<form>` submit. */
  @Prop({ reflect: true }) name: string | undefined;

  /** Wether or not the color picker is visible or not. */
  @Prop({ reflect: true }) open: boolean = false;

  /** Whether or not the `value` of the component can be set, or if it can only be read. */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  /** The selected color. */
  @Prop({ reflect: true }) value: string = '';

  @State() stateValue: string = '';

  /** Whether or not the component is disabled. */
  @Prop({ reflect: true }) disabled?: boolean;

  connectedCallback() {}

  getValidHexColor(value: string): string {
    return value
      .replace(/[^0-9A-Fa-f]/g, '')
      .substring(0, 6)
      .toUpperCase();
  }

  componentDidLoad() {
    this.stateValue = this.getValidHexColor(this.value);
    this.value = this.stateValue;
    // If the embedded menu is being opened, then ensure the color picker is also opened.
    this.menuContentElem?.addEventListener('aha-menu-content:open', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.openEvent.emit(this.host);
    });

    // If the embedded menu is fully opened, fire off the opened event for the color picker
    this.menuContentElem?.addEventListener('aha-menu-content:opened', e => {
      this.openedEvent.emit(this.host);
    });

    // If the embedded menu is being closed, then ensure the color picker is also closed.
    this.menuContentElem?.addEventListener('aha-menu-content:close', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.closeEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:closed', e => {
      this.closedEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:cancel', (event: any) => {
      if (event.detail) {
        // @ts-ignore
        const menu = window.portalClosest(event.detail, 'aha-menu');
        if (!menu) return;
      }
      this.cancelEvent.emit(this.host);
    });
  }

  handleClick(color: string) {
    this.stateValue = color;
    this.value = this.stateValue;
    this.open = false;
    this.changeEvent.emit(this.host);
  }

  handleColorChange(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = this.getValidHexColor(input.value);

    this.stateValue = value;
    this.value = this.stateValue;
    this.changeEvent.emit(this.host);
  }

  render() {
    return (
      <Host>
        <slot name="form_control">
          {/* The hidden field that is used to pass the value to any parent <form> element. */}
          <input
            type="hidden"
            value={`#${this.stateValue}`}
            name={this.name}
            readOnly={this.readOnly}
            disabled={this.disabled}
          />
        </slot>
        <aha-menu open={this.open} disabled={this.readOnly || this.disabled}>
          <aha-button
            slot="control"
            disabled={this.disabled}
            onClick={() => (this.open = !this.open)}
          >
            <div class="tw-flex tw-items-center tw-gap-1">
              <div class="pickable-color" style={{ backgroundColor: `#${this.stateValue}` }}></div>
              <aha-icon icon="fa-solid fa-caret-down"></aha-icon>
            </div>
          </aha-button>
          <aha-menu-content
            unrestricted-scroll
            ref={ref => (this.menuContentElem = ref)}
            style={{
              // Add extra padding between the popup and the triggering element to accomodate the
              // triggering elements focus state border
              '--aha-menu-content--vertical-margin': '2px',
            }}
          >
            <div class="tw-flex tw-flex-col tw-gap-2 tw-p-3">
              <div class="tw-flex tw-flex-wrap tw-gap-1 tw-max-w-[270px]">
                {COLORS.map(color => (
                  <div
                    class={'pickable-color tw-cursor-pointer'}
                    style={{ backgroundColor: `#${color}` }}
                    onClick={() => this.handleClick(color)}
                  ></div>
                ))}
              </div>
              <div class="tw-flex tw-items-center tw-gap-2">
                <div class="tw-relative">
                  {/* Clickable color preview square at bottom to open color wheel */}
                  <div
                    class="pickable-color tw-cursor-pointer"
                    style={{ backgroundColor: `#${this.stateValue}` }}
                  ></div>
                  <input
                    type="color"
                    value={this.stateValue}
                    class="tw-absolute tw-inset-0 tw-opacity-0 tw-w-[17px] tw-h-[17px] tw-cursor-pointer"
                    onChange={event => this.handleColorChange(event)}
                  />
                </div>
                <input
                  class="input-small aha-color-picker__input-text"
                  type="text"
                  value={`#${this.stateValue}`}
                  onInput={event => this.handleColorChange(event)}
                />
              </div>
            </div>
          </aha-menu-content>
        </aha-menu>
      </Host>
    );
  }
}
